import React, { useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	Button,
	Checkbox,
	FormControlLabel,
	Alert,
	Box,
} from '@barracuda-internal/bds-core-v2';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import { FeedStatus, FeedStatusChangeReason } from 'src/types/dataTypes';

interface RemediatePopupProps {
	open: boolean;
	selectedFeeds: string[];
	remediateOption: string;
	onClose: (refresh: boolean) => void;
}

export default function RemediatePopup({
	open,
	selectedFeeds,
	remediateOption,
	onClose,
}: RemediatePopupProps): JSX.Element {
	const { api } = useAppToolchain();
	const [showConfirm, setShowConfirm] = useState(false);
	const [authError, setAuthError] = useState(false);
	const [otherError, setOtherError] = useState(false);
	const [loading, setLoading] = useState(false);

	const handleRemediate = async () => {
		let status: FeedStatus = 'Dismissed';
		let statusReason: FeedStatusChangeReason = 'UserAction';

		switch (remediateOption) {
			case 'Delete':
				status = 'RemediationInitiated';
				statusReason = 'UserDeleted';
				break;
			case 'Quarantine':
				status = 'RemediationInitiated';
				statusReason = 'UserQuarantined';
				break;
			case 'UnShare':
				status = 'RemediationInitiated';
				statusReason = 'UserUnShared';
				break;
			case 'Dismiss':
			default:
				status = 'Dismissed';
				statusReason = 'UserAction';
				break;
		}

		setLoading(true);
		try {
			await api.updateBulkFeed(selectedFeeds, status, statusReason);
			setAuthError(false);
			setOtherError(false);
			setShowConfirm(false);
			onClose(true);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			if (err?.response?.status === 403) {
				setAuthError(true);
			} else {
				setOtherError(true);
			}
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		setAuthError(false);
		setOtherError(false);
		setShowConfirm(false);
		onClose(false);
	};

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	const noop = () => {};

	return (
		<Dialog open={open} onClose={noop}>
			<DialogTitle>Remediate automatically</DialogTitle>
			<DialogContent sx={{ py: 0 }}>
				<Box sx={{ pt: 0, px: 2, pb: 0 }}>
					{otherError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							There was an error processing your request. Please
							try again.
						</Alert>
					)}
					{authError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							Requested action not allowed as permissions required
							to perform this action are not consented. Please
							update your tenant&apos;s consented permissions by
							reauthorizing tenant connector from{' '}
							<a href="/settings/connectors">Connectors</a> page.
						</Alert>
					)}
					<Typography variant="body1" sx={{ mb: 2 }}>
						You are about to remediate <b>{selectedFeeds.length}</b>{' '}
						files
					</Typography>
					{remediateOption === 'Delete' && (
						<Typography variant="body1" sx={{ mb: 2 }}>
							Remediated files will be deleted from the
							user&apos;s drive and moved to Recycle Bin.
						</Typography>
					)}
					{remediateOption === 'Quarantine' && (
						<Typography variant="body1" sx={{ mb: 2 }}>
							Remediated files will be moved to
							&quot;DI_QUARANTINE&quot; folder which will be
							present at the root level.
						</Typography>
					)}
					{remediateOption === 'UnShare' && (
						<Typography variant="body1" sx={{ mb: 2 }}>
							Remediated files will have their sharing permissions
							and links removed and be restricted to Private and
							only the owners will have access.
						</Typography>
					)}
					{remediateOption === 'Dismiss' && (
						<Typography variant="body1" sx={{ mb: 2 }}>
							No action will be taken on the file in the
							user&apos;s drive. The feed will be dismissed and
							will be shown under Resolved detections.
						</Typography>
					)}
					{remediateOption !== 'Dismiss' && (
						<FormControlLabel
							sx={{ ml: 0 }}
							control={
								<Checkbox
									checked={showConfirm}
									onChange={() =>
										setShowConfirm(!showConfirm)
									}
									sx={{ mr: 1 }}
								/>
							}
							label="Continue with your action"
						/>
					)}
				</Box>
			</DialogContent>
			<DialogActions sx={{ m: 1, pt: 0 }}>
				<Button
					bdsType="interactiveNeutral"
					disabled={loading}
					onClick={handleCancel}
				>
					Cancel
				</Button>
				<Button
					bdsType="interactiveEmphasis"
					disabled={
						!(remediateOption === 'Dismiss' || showConfirm) ||
						loading
					}
					onClick={handleRemediate}
				>
					{loading ? 'Remediating...' : 'Remediate'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
